/**
 * Derives the inbound-FK list surfaced in the Related Data panel.
 *
 * Slice 1 ships declared FKs only — `isLoose` is always false. Slice 6 layers
 * the MetaInfo loose-FK merge on top (kept here so the loose path becomes a
 * one-spot change rather than a fresh hook).
 *
 * Results are sorted by `{schema}.{table}.{constraint}` for a stable display
 * order regardless of map-iteration noise.
 */

import { useMemo } from 'react';
import type { TableExplorerInboundForeignKey } from '../../../../types/tableExplorer';
import type { InboundRelation } from '../types/singleRowTypes';

export type UseInboundRelationsParams = {
  readonly inboundForeignKeys: ReadonlyArray<TableExplorerInboundForeignKey>;
};

/**
 * Pure helper extracted for unit testing — `useInboundRelations` simply
 * memoizes this.
 */
export function buildInboundRelations(
  inboundForeignKeys: ReadonlyArray<TableExplorerInboundForeignKey>,
): ReadonlyArray<InboundRelation> {
  const relations: InboundRelation[] = inboundForeignKeys.map((fk) => {
    const fromColumn = fk.referencingColumns[0] ?? '';
    const toColumn = fk.referencedColumns[0] ?? '';
    return {
      relationKey: `${fk.constraintName}|${fk.referencingSchema}|${fk.referencingTable}|${fromColumn}`,
      fromSchema: fk.referencingSchema,
      fromTable: fk.referencingTable,
      fromColumn,
      toSchema: fk.referencedSchema,
      toTable: fk.referencedTable,
      toColumn,
      // Slice 1 emits declared FKs only — the upstream API may still mark some
      // as loose (the IPC layer merges them), but Slice 6 will be the one to
      // properly surface that via the breadcrumb. For Slice 1 we honor the
      // upstream isLoose flag so behavior stays consistent with existing UI.
      isLoose: fk.isLoose === true,
      // Concern 2 — surface the referencing-table PK so the EP6 mini-grid drill
      // can build the row-node PK from the full composite PK. Defaults to []
      // when the backend didn't enrich; consumers fall back to first-column.
      targetPkColumns: fk.referencingTablePrimaryKey ?? [],
      source: fk,
    };
  });

  relations.sort((a, b) => {
    const aKey = `${a.fromSchema}.${a.fromTable}.${a.relationKey}`;
    const bKey = `${b.fromSchema}.${b.fromTable}.${b.relationKey}`;
    return aKey.localeCompare(bKey);
  });

  return relations;
}

export function useInboundRelations(
  params: UseInboundRelationsParams,
): ReadonlyArray<InboundRelation> {
  const { inboundForeignKeys } = params;
  return useMemo(() => buildInboundRelations(inboundForeignKeys), [inboundForeignKeys]);
}
